<template>
	<v-card flat>
		<v-card-title class="employee-forms-list__header">
			<div>
				<div class="title text-xs-left">Forms</div>
				<div class="caption text--secondary">{{ resultSummaryText }}</div>
			</div>
			<v-spacer></v-spacer>
			<v-btn v-if="canAssign" color="primary" @click="onAssignClick">Assign Form</v-btn>
		</v-card-title>

		<v-card-text v-if="forms.length === 0 && !isLoading" class="text-xs-center py-5">
			<div class="subheading mb-2">No forms assigned yet</div>
			<div class="text--secondary mb-4">Assign a form to {{ employeeFirstName }} to start tracking it here.</div>
			<v-btn v-if="canAssign" color="primary" @click="onAssignClick">Assign First Form</v-btn>
		</v-card-text>

		<v-card-text v-else class="employee-forms-list__body">
			<div class="employee-forms-list__summary mb-2">
				<v-chip small>{{ outstandingFormsCount }} require action</v-chip>
				<v-chip v-if="submittedFormsCount > 0" small color="success" text-color="white">{{ submittedFormsCount }} submitted</v-chip>
				<v-chip v-if="cancelledFormsCount > 0" small color="error" text-color="white">{{ cancelledFormsCount }} cancelled</v-chip>
			</div>

			<div class="employee-forms-list__filters">
				<v-text-field v-model="search" append-icon="search" label="Search forms" single-line hide-details
					class="employee-forms-list__search mb-0"></v-text-field>
				<v-select v-model="categoryFilter" :items="categoryOptions"
					class="employee-forms-list__category-filter mb-0" label="Category" item-text="text"
					item-value="value" clearable single-line hide-details no-data-text="No categories"></v-select>
				<v-select v-model="statusFilter" :items="statusOptions" class="employee-forms-list__status-filter mb-0"
					label="Status" item-text="text" item-value="value" clearable single-line hide-details></v-select>
				<v-btn v-if="hasActiveFilters" flat small class="employee-forms-list__clear-filters" @click="clearFilters">
					Clear filters
				</v-btn>
			</div>

			<v-data-table disable-initial-sort :headers="headers" :items="visibleForms" :loading="isLoading"
				:pagination.sync="pagination" :rows-per-page-items="rowsPerPageItems" class="employee-forms-list__table">
				<template slot="items" slot-scope="props">
					<tr>
						<td class="text-xs-left">
							<a href="" class="font-weight-medium" @click.prevent="openSubmission(props.item)">{{ props.item.formName }}</a>
						</td>
						<td class="text-xs-left">
							<v-chip v-if="getCategoryName(props.item)" small label class="employee-forms-list__category-chip"
								@click.native.stop="applyCategoryFilter(props.item)">
								{{ getCategoryName(props.item) }}<span v-if="props.item.categoryIsDeleted">&nbsp;(archived)</span>
							</v-chip>
							<v-chip v-else small label class="employee-forms-list__category-chip employee-forms-list__category-chip--muted"
								@click.native.stop="applyCategoryFilter(props.item)">
								Uncategorised
							</v-chip>
						</td>
						<td class="text-xs-left">
							<v-chip small :dark="getStatusChipColor(props.item) !== ''" :color="getStatusChipColor(props.item)"
								class="employee-forms-list__status-chip" @click.native.stop="applyStatusFilter(props.item)">
								{{ getStatusFriendly(props.item) }}
							</v-chip>
						</td>
						<td class="text-xs-left"><AppDate :date="props.item.createdDateUtc" /></td>
						<td class="text-xs-left">
							<span v-if="props.item.submittedDateUtc"><AppDate :date="props.item.submittedDateUtc" /></span>
							<span v-else class="text--secondary">Not submitted</span>
						</td>
						<td class="text-xs-right form-actions-cell">
							<v-btn v-if="canCompleteForm(props.item)" small color="primary" @click="openSubmission(props.item)">Complete</v-btn>
							<v-menu v-if="getAvailableActionsForForm(props.item).length > 0" offset-y>
								<template v-slot:activator="{ on }">
									<v-btn small icon flat :loading="isSendingNewLink || isCancellingFormSubmission" v-on="on">
										<v-icon>mdi-dots-horizontal</v-icon>
									</v-btn>
								</template>
								<v-list>
									<v-list-tile v-for="(action, index) in getAvailableActionsForForm(props.item)" :key="index" @click="action.callback">
										<v-list-tile-action>
											<v-icon>{{ action.icon }}</v-icon>
										</v-list-tile-action>
										<v-list-tile-title :class="action.titleClass">{{ action.title }}</v-list-tile-title>
									</v-list-tile>
								</v-list>
							</v-menu>
						</td>
					</tr>
				</template>
				<template slot="no-data">
					<div class="employee-forms-list__empty-state pa-4">
						<div class="subheading mb-1">{{ noDataTitle }}</div>
						<div class="text--secondary">{{ noDataText }}</div>
						<v-btn v-if="hasActiveFilters" flat small color="primary" class="mt-2" @click="clearFilters">
							Clear filters
						</v-btn>
					</div>
				</template>
			</v-data-table>
		</v-card-text>

		<form-assignment-dialog
			v-if="showAssignDialog"
			:dialog="showAssignDialog"
			:title="assignDialogTitle"
			:forms="availableForms"
			:employees="employees"
			:fixed-employee-id="employeeId"
			:fixed-employee-name="employeeName"
			:fixed-employee-manager-id="employeeManagerId"
			:fixed-employee-manager-name="employeeManagerName"
			:is-assigning="isAssigning"
			@assign="$emit('assign', $event)"
			@close="closeDialog"
		/>
	</v-card>
</template>

<script>
import FormAssignmentDialog from '@/components/forms/FormAssignmentDialog.vue';

export default {
	name: 'EmployeeAssignedFormsList',
	components: {
		FormAssignmentDialog
	},
	props: {
		forms: {
			type: Array,
			default: () => []
		},
		availableForms: {
			type: Array,
			default: () => []
		},
		employees: {
			type: Array,
			default: () => []
		},
		employeeId: {
			type: String,
			default: null
		},
		employeeManagerId: {
			type: String,
			default: null
		},
		employeeManagerName: {
			type: String,
			default: ''
		},
		isLoading: {
			type: Boolean,
			default: false
		},
		isAssigning: {
			type: Boolean,
			default: false
		},
		isSendingNewLink: {
			type: Boolean,
			default: false
		},
		isCancellingFormSubmission: {
			type: Boolean,
			default: false
		},
		canAssign: {
			type: Boolean,
			default: false
		},
		employeeName: {
			type: String,
			default: 'this employee'
		},
		employeeFirstName: {
			type: String,
			default: 'this employee'
		},
		tenantId: {
			type: String,
			required: true
		},
		isEmployeeRespondent: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			showAssignDialog: false,
			search: '',
			categoryFilter: null,
			statusFilter: null,
			pagination: {
				sortBy: 'createdDateUtc',
				descending: true,
				rowsPerPage: 25
			},
			rowsPerPageItems: [10, 25, 50, { text: '$vuetify.dataIterator.rowsPerPageAll', value: -1 }]
		};
	},
	computed: {
		headers() {
			return [
				{ text: 'Name', value: 'formName' },
				{ text: 'Category', value: 'categoryName' },
				{ text: 'Status', value: 'state' },
				{ text: 'Assigned', value: 'createdDateUtc' },
				{ text: 'Submitted', value: 'submittedDateUtc' },
				{ text: '', value: 'actions', sortable: false, align: 'right' }
			];
		},
		assignDialogTitle() {
			return `Assign Form to ${this.employeeName}`;
		},
		outstandingFormsCount() {
			return this.forms.filter(this.isOutstandingForm).length;
		},
		submittedFormsCount() {
			return this.forms.filter(this.isSubmittedForm).length;
		},
		cancelledFormsCount() {
			return this.forms.filter(form => form.state === 'Cancelled').length;
		},
		visibleForms() {
			let forms = this.forms;

			if (this.categoryFilter) {
				forms = forms.filter(form => this.getCategoryFilterValue(form) === this.categoryFilter);
			}

			if (this.statusFilter) {
				forms = forms.filter(form => this.getStatusFilterValue(form) === this.statusFilter);
			}

			if (this.normalisedSearch) {
				forms = forms.filter(form => this.formMatchesSearch(form));
			}

			return forms;
		},
		categoryOptions() {
			const categoriesById = {};
			let hasUncategorised = false;

			this.forms.forEach(form => {
				if (!form) return;

				const value = this.getCategoryFilterValue(form);
				if (value === '__uncategorised') {
					hasUncategorised = true;
					return;
				}

				if (!categoriesById[value]) {
					categoriesById[value] = {
						text: this.getCategoryName(form),
						value
					};
				}
			});

			const options = Object.values(categoriesById)
				.filter(option => !!option.text)
				.sort((a, b) => a.text.localeCompare(b.text));

			if (hasUncategorised) {
				options.unshift({
					text: 'Uncategorised',
					value: '__uncategorised'
				});
			}

			return options;
		},
		statusOptions() {
			const statusesByValue = {};

			this.forms.forEach(form => {
				if (!form) return;

				const value = this.getStatusFilterValue(form);
				if (!statusesByValue[value]) {
					statusesByValue[value] = {
						text: this.getStatusFriendly(form),
						value
					};
				}
			});

			const order = ['LinkExpired', 'NotSubmitted', 'Draft', 'Submitted', 'Cancelled'];
			return Object.values(statusesByValue).sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value));
		},
		hasActiveFilters() {
			return !!this.categoryFilter || !!this.statusFilter || !!this.normalisedSearch;
		},
		normalisedSearch() {
			return this.search.trim().toLowerCase();
		},
		selectedCategoryText() {
			const selected = this.categoryOptions.find(option => option.value === this.categoryFilter);
			return selected ? selected.text : null;
		},
		selectedStatusText() {
			const selected = this.statusOptions.find(option => option.value === this.statusFilter);
			return selected ? selected.text : null;
		},
		resultSummaryText() {
			const total = this.forms.length;
			const visible = this.visibleForms.length;
			const noun = total === 1 ? 'form assignment' : 'form assignments';

			if (this.hasActiveFilters) {
				return `Showing ${visible} of ${total} ${noun}`;
			}

			return `${total} ${noun}`;
		},
		noDataTitle() {
			return this.hasActiveFilters ? 'No assigned forms match these filters' : 'No forms assigned yet';
		},
		noDataText() {
			if (!this.hasActiveFilters) {
				return `Assign a form to ${this.employeeFirstName} to start tracking it here.`;
			}

			const details = [];
			if (this.normalisedSearch) details.push(`"${this.search}"`);
			if (this.selectedCategoryText) details.push(this.selectedCategoryText);
			if (this.selectedStatusText) details.push(this.selectedStatusText);

			return details.length
				? `No assigned forms match ${details.join(', ')}.`
				: 'No assigned forms match the selected filters.';
		}
	},
	watch: {
		search() {
			this.pagination.page = 1;
		},
		categoryFilter() {
			this.pagination.page = 1;
		},
		statusFilter() {
			this.pagination.page = 1;
		}
	},
	methods: {
		getAvailableFormForAssignment(item) {
			return this.availableForms.find(form => form && form.id === item.formId) || null;
		},
		getCategoryName(item) {
			const availableForm = this.getAvailableFormForAssignment(item);
			return item.categoryName || (availableForm && availableForm.categoryName) || '';
		},
		getCategoryFilterValue(item) {
			const availableForm = this.getAvailableFormForAssignment(item);
			const categoryName = this.getCategoryName(item);

			if (!categoryName) return '__uncategorised';

			return item.categoryId || (availableForm && availableForm.categoryId) || categoryName;
		},
		applyCategoryFilter(item) {
			this.categoryFilter = this.getCategoryFilterValue(item);
		},
		getStatusFilterValue(item) {
			if (this.isLinkExpired(item)) return 'LinkExpired';
			return item.state || '';
		},
		applyStatusFilter(item) {
			this.statusFilter = this.getStatusFilterValue(item);
		},
		clearFilters() {
			this.search = '';
			this.categoryFilter = null;
			this.statusFilter = null;
		},
		formMatchesSearch(item) {
			const searchableText = [
				item.formName,
				item.formDescription,
				this.getCategoryName(item) || 'Uncategorised',
				this.getStatusFriendly(item),
				item.formVersionNo ? `v${item.formVersionNo}` : ''
			]
				.filter(Boolean)
				.join(' ')
				.toLowerCase();

			return searchableText.includes(this.normalisedSearch);
		},
		isSubmittedForm(item) {
			return item.state === 'Submitted' || !!item.submittedDateUtc;
		},
		isOutstandingForm(item) {
			return !this.isSubmittedForm(item) && item.state !== 'Cancelled';
		},
		getStatusFriendly(item) {
			if (this.isLinkExpired(item)) return 'Link expired';

			switch (item.state) {
				case 'NotSubmitted':
					return 'Assigned';
				case 'Draft':
					return 'In Progress';
				case 'Submitted':
					return 'Submitted';
				case 'Cancelled':
					return 'Cancelled';
				default:
					return item.state || '';
			}
		},
		getStatusChipColor(item) {
			if (this.isLinkExpired(item)) return 'error';

			switch (item.state) {
				case 'Submitted':
					return 'success';
				case 'Draft':
					return 'warning';
				case 'Cancelled':
					return 'error';
				case 'NotSubmitted':
				default:
					return '';
			}
		},
		canCompleteForm(item) {
			const actionableStates = ['NotSubmitted', 'Draft'];
			return this.isEmployeeRespondent && actionableStates.includes(item.state) && !item.submittedDateUtc;
		},
		canSendNewLink(item) {
			return this.canAssign && item && ['NotSubmitted', 'Draft'].includes(item.state) && !item.submittedDateUtc;
		},
		canCancelFormSubmission(item) {
			return this.canAssign && item && ['NotSubmitted', 'Draft'].includes(item.state) && !item.submittedDateUtc;
		},
		isLinkExpired(item) {
			return !!item && this.canSendNewLink(item) && item.isPasskeyExpired;
		},
		getAvailableActionsForForm(item) {
			const actions = [];

			if (this.canSendNewLink(item)) {
				actions.push({
					title: 'Send new link',
					icon: 'mail',
					callback: () => this.$emit('sendNewLink', item)
				});
			}

			if (this.canCancelFormSubmission(item)) {
				actions.push({
					title: 'Cancel assignment',
					icon: 'mdi-cancel',
					titleClass: 'error--text',
					callback: () => this.$emit('cancelSubmission', item)
				});
			}

			return actions;
		},
		openSubmission(item) {
			this.$emit('openSubmission', {
				submissionId: item.submissionId,
				isReadonly: !this.canCompleteForm(item)
			});
		},
		onAssignClick() {
			this.$emit('openAssign');
			this.showAssignDialog = true;
		},
		closeDialog() {
			this.showAssignDialog = false;
		}
	}
};
</script>

<style scoped>
.form-actions-cell {
	width: 1px;
	white-space: nowrap;
	padding-left: 0;
	padding-right: 0;
}
.employee-forms-list__header {
	align-items: center;
	padding-bottom: 8px;
}
.employee-forms-list__body {
	padding-top: 0;
}
.employee-forms-list__summary,
.employee-forms-list__filters {
	display: flex;
	gap: 12px;
	align-items: center;
	justify-content: flex-end;
}
.employee-forms-list__filters {
	margin-bottom: 10px;
	min-height: 48px;
}
.employee-forms-list__search {
	max-width: 420px;
}
.employee-forms-list__category-filter {
	max-width: 240px;
}
.employee-forms-list__status-filter {
	max-width: 180px;
}
.employee-forms-list__clear-filters {
	flex: 0 0 auto;
}
.employee-forms-list__table {
	margin-top: 8px;
}
.employee-forms-list__category-chip,
.employee-forms-list__status-chip {
	cursor: pointer;
}
.employee-forms-list__category-chip--muted {
	color: rgba(0, 0, 0, 0.54);
}
.employee-forms-list__empty-state {
	text-align: center;
}
@media (max-width: 959px) {
	.employee-forms-list__summary,
	.employee-forms-list__filters {
		align-items: stretch;
		flex-direction: column;
	}

	.employee-forms-list__search,
	.employee-forms-list__category-filter,
	.employee-forms-list__status-filter {
		max-width: none;
	}
}
</style>
